import { isAfter, isBefore, isSameTime, stringToLocalDate } from "@hex-insights/core";
import { CalendarEvent, CalendarEventNameFragment } from "../../../Utilities";
import * as Fields from "./fields";

export { Fields };

/**
 * Formats the full name of the given instance.
 */
export function name(instance: CalendarEventNameFragment) {
	return Fields.name(instance.name);
}

export function status(instance: Pick<CalendarEvent, "startTime" | "endTime" | "startDate" | "endDate">) {
	const start = instance.startDate
		? stringToLocalDate(instance.startDate, "day")
		: instance.startTime
		? new Date(instance.startTime)
		: new Date();
	const end = instance.endDate
		? stringToLocalDate(instance.endDate, "day")
		: instance.endTime
		? new Date(instance.endTime)
		: new Date();

	if (isBefore(new Date(), start)) {
		return "Upcoming";
	}
	if (isAfter(new Date(), end)) {
		return "Past";
	}
	return "Now";
}

const dateFormat: Intl.DateTimeFormatOptions = { month: "long", day: "numeric", weekday: "long" };
const timeFormat: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };

export function timeRange(
	instance: Pick<CalendarEvent, "isAllDay" | "startTime" | "endTime" | "startDate" | "endDate">,
) {
	if (instance.isAllDay && instance.startDate && instance.endDate) {
		const startDate = stringToLocalDate(instance.startDate, "day");
		const endDate = stringToLocalDate(instance.endDate, "day");
		const startsThisYear = isSameTime(startDate, new Date(), "year");
		const formattedStartDate = startDate.toLocaleString(undefined, {
			...dateFormat,
			year: startsThisYear ? undefined : "numeric",
		});
		if (isSameTime(startDate, endDate, "day")) {
			return formattedStartDate;
		}
		const endsThisYear = isSameTime(endDate, new Date(), "year");
		const formattedEndDate = endDate.toLocaleString(undefined, {
			...dateFormat,
			year: endsThisYear ? undefined : "numeric",
		});
		return `${formattedStartDate} - ${formattedEndDate}`;
	}

	if (instance.startTime && instance.endTime) {
		const startTime = new Date(instance.startTime);
		const endTime = new Date(instance.endTime);
		const startsThisYear = isSameTime(startTime, new Date(), "year");
		const formattedStartDate = startTime.toLocaleString(undefined, {
			...dateFormat,
			year: startsThisYear ? undefined : "numeric",
		});
		const formattedStartTime = startTime.toLocaleString(undefined, timeFormat);
		const formattedEndTime = endTime.toLocaleString(undefined, timeFormat);
		if (isSameTime(startTime, endTime, "day")) {
			return `${formattedStartDate} ${formattedStartTime} - ${formattedEndTime}`;
		}
		const endsThisYear = isSameTime(endTime, new Date(), "year");
		const formattedEndDate = endTime.toLocaleString(undefined, {
			...dateFormat,
			year: endsThisYear ? undefined : "numeric",
		});
		return `${formattedStartDate} ${formattedStartTime} - ${formattedEndDate} ${formattedEndTime}`;
	}

	return "";
}

/**
 * Formats the time range of the given instance relative to the given date.
 *
 * @note Does not take into account whether the instance actually occurs on the given date.
 */
export function timeRangeOnDate(
	instance: Pick<CalendarEvent, "isAllDay" | "startTime" | "endTime" | "startDate" | "endDate">,
	date: Date,
) {
	if (instance.isAllDay) {
		return "All Day";
	}
	if (instance.startTime && instance.endTime) {
		const startTime = new Date(instance.startTime);
		const formattedStartTime = startTime.toLocaleString(undefined, timeFormat);
		const startsOnDate = isSameTime(startTime, date, "day");
		const endTime = new Date(instance.endTime);
		const formattedEndTime = endTime.toLocaleString(undefined, timeFormat);
		const endsOnDate = isSameTime(endTime, date, "day");
		if (startsOnDate && endsOnDate) {
			return `${formattedStartTime} - ${formattedEndTime}`;
		}
		if (startsOnDate) {
			return `Starts ${formattedStartTime}`;
		}
		if (endsOnDate) {
			return `Ends ${formattedEndTime}`;
		}
		return "All Day";
	}
	return "";
}
